import cx from "classnames";
import { useId } from "react";

import styles from "./styles.module.css";

const SPARKLE_PATH =
  "M60.0742 30C61.4033 30.0001 62.6783 30.528 63.6182 31.4678C64.5582 32.4078 65.0859 33.6833 65.0859 35.0127C65.086 40.3298 67.1983 45.4296 70.958 49.1895C74.686 52.9174 79.7316 55.021 85 55.0566V55.0742L85.2568 55.0801C87.8989 55.2138 90 57.3989 90 60.0742C89.9998 62.8355 87.7613 65.0742 85 65.0742V65.0859C79.7316 65.1215 74.686 67.23 70.958 70.958C67.1982 74.7178 65.0862 79.8177 65.0859 85.1348C65.0859 86.4641 64.5581 87.7397 63.6182 88.6797C62.6783 89.6196 61.4034 90.1473 60.0742 90.1475C58.7449 90.1474 57.4692 89.6196 56.5293 88.6797C55.5895 87.7397 55.0615 86.4639 55.0615 85.1348C55.0613 79.8177 52.9492 74.7178 49.1895 70.958C45.4296 67.1983 40.3298 65.0861 35.0127 65.0859C33.6833 65.0859 32.4078 64.5582 31.4678 63.6182C30.528 62.6783 30.0001 61.4033 30 60.0742C30.0001 58.745 30.5279 57.4692 31.4678 56.5293C32.4078 55.5894 33.6834 55.0615 35.0127 55.0615C40.33 55.0614 45.4296 52.9493 49.1895 49.1895C52.9493 45.4296 55.0614 40.33 55.0615 35.0127C55.0615 33.6834 55.5894 32.4078 56.5293 31.4678C57.4692 30.5278 58.745 30.0001 60.0742 30Z";

type InfraAssistantAnimatedIconProps = React.SVGProps<SVGSVGElement> & {
  /** Freezes the gradient rotation and sparkle motion (e.g. when the host button is disabled). */
  paused?: boolean;
};

const InfraAssistantAnimatedIcon = ({
  className,
  paused,
  ...rest
}: InfraAssistantAnimatedIconProps) => {
  const id = useId();

  return (
    <svg
      className={cx(styles.root, paused && styles.paused, className)}
      viewBox="0 0 120 120"
      aria-hidden="true"
      {...rest}
    >
      <defs>
        <linearGradient
          id={`${id}-grad`}
          gradientUnits="userSpaceOnUse"
          x1="100"
          y1="20"
          x2="20"
          y2="100"
        >
          <stop
            offset="0%"
            stopColor="var(--semantic-color-foreground-ai-icon-gradient-from, #F0883E)"
          />
          <stop
            offset="100%"
            stopColor="var(--semantic-color-foreground-ai-icon-gradient-to, #7C47FC)"
          />
        </linearGradient>
        <filter id={`${id}-goo`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -9"
          />
        </filter>
        {/* The mask is the two sparkle shapes only (no filter inside it). A filter
            in a mask renders a frame late on mount, during which the masked rect
            paints its raw edges at the icon boundary — the flash. Keeping the mask
            filter-free means it applies on the first paint frame. */}
        <mask
          id={`${id}-mask`}
          maskUnits="userSpaceOnUse"
          x="-60"
          y="-60"
          width="240"
          height="240"
        >
          <path className={styles.sparkle2} d={SPARKLE_PATH} fill="white" />
          <path className={styles.sparkle1} d={SPARKLE_PATH} fill="white" />
        </mask>
      </defs>
      {/* The gooey merge filter is applied to the already-masked, gradient-colored
          result (not inside the mask). Worst case before it renders is a single
          frame of sharp sparkles — never a stray rect edge. */}
      <g className={styles.gooGroup} filter={`url(#${id}-goo)`}>
        <g mask={`url(#${id}-mask)`}>
          {/* Rotating the gradient-filled rect (userSpaceOnUse gradient) spins the
              color sweep; a CSS animation lets reduced-motion + Chromatic freeze it. */}
          <g className={styles.gradientRotate}>
            <rect x="0" y="0" width="120" height="120" fill={`url(#${id}-grad)`} />
          </g>
        </g>
      </g>
    </svg>
  );
};

InfraAssistantAnimatedIcon.displayName = "InfraAssistantAnimatedIcon";

export default InfraAssistantAnimatedIcon;
